// livestreamSettingsSlice.ts
import { rest } from "lodash";
import Mode from "../../components/classroom/model/Mode";
import SeatingStyle from "../../components/classroom/model/SeatingStyle";
import axiosInstance from "../../config/axios";
import { compose, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ro } from "date-fns/locale";
import { json } from "stream/consumers";
import { sessionModeType } from "types/Constants";


// Interface for the data which is to be stored in store for livestream settings page.

interface ISegment {
  [key: string]: any;
}

interface ISession {
  [key: string]: any
  mapID: string;
  startDate: string;
  segments: ISegment[];
  endDate: string;
  startTime: string,
  endTime: string
}

interface IParticipant {
  [key: string]: any
}

interface IBatch {
  [key: string]: any
}

interface ITrainer {
  [key: string]: any
}

interface IPeer {
  id: string;
  displayName: string;
  device: string;
  consumers: string[]; // Array of consumer IDs associated with this peer
}

interface IConsumer {
  id: string;
  peerId: string;
  kind: 'audio' | 'video';
  type: string;
  locallyPaused: boolean;
  remotelyPaused: boolean;
  rtpParameters: any;
  spatialLayers: number;
  temporalLayers: number;
  preferredSpatialLayer: number;
  preferredTemporalLayer: number;
  priority: number;
  codec: string;
  track: MediaStreamTrack;
  producerId: string;
}

export interface LiveStreamSettingsState {
  roomId?: string;
  error: string | null;
  isLoading: boolean
  mode: Mode;
  seatingStyle: SeatingStyle;
  session: ISession | null;
  trainer: ITrainer | null;
  batch: IBatch | null;
  video: boolean;
  audio: boolean;
  onSpeaker: boolean;
  badgesPanel: boolean;
  hasNewBadge: boolean;
  isMicAccessGranted: boolean;
  isCameraAccessGranted: boolean;
  isScreenPresented: boolean
  leftPanel: string | null;
  closeEntry: boolean;
  muteParticipants: boolean;
  allowSharingScreen: boolean;
  onlineParticipants: number;
  recordingOptions: {
    recordSession: boolean;
    recordGroupInteraction: boolean;
  };
  participantEntryType?: string;
  peersNew: { [key: string]: IPeer };
  consumers: { [key: string]: IConsumer };
  theme: {
    theme: string;
    primarycolor: string
  }
  logo: string;
  producers: any;
  batchDetails: any;
  participantData: any;
  sessionData: any;
  sessionSettingData: any;
  allGroup: any[];
  myGroup: any;
  groupLeader: any;
  consoleDetails: any;
  participants: any;
  userStream: MediaStream;
  roomState: string;
  backgroundEffect: boolean;
  sessionLiveFlag: boolean;
  bgImage: {
    [user: string]: string
  };
  pendingBg: {
    [user: string]: string
  };
  liveSessionActivityData: any;
  selectedAudioDevice: any;
  audioOutputDevices: any;
  selectedMicDevice: any;
  micInputDevices: any;
  audioSwitchInProgress: any;
  trainerJoinedtable: any;
  activeQueries: {
    [queryType: string]: {
      raisedAt: number;
      timeoutId?: NodeJS.Timeout;
    };
  };
  queryPopups: {
    [queryType: string]: {
      visible: boolean;
      originalQuery: string;
      raisedAt: number;
    };
  };
  consumerPeers: any;
  groupLeaderNew: any;
}

// Defining the initial default state in store.
const initialState: LiveStreamSettingsState = {
  mode: Mode.NETWORKING,
  isLoading: false,
  error: null,
  seatingStyle: SeatingStyle.CLUSTER,
  session: null,
  trainer: null,
  batch: null,
  video: true,
  audio: true,
  onSpeaker: true,
  isScreenPresented: false,
  isMicAccessGranted: false,
  isCameraAccessGranted: false,
  leftPanel: null,
  badgesPanel: false,
  hasNewBadge: false,
  closeEntry: false,
  muteParticipants: false,
  allowSharingScreen: false,
  onlineParticipants: 0,
  recordingOptions: {
    recordSession: false,
    recordGroupInteraction: false,
  },
  peersNew: {},
  consumers: {},
  theme: {
    theme: '',
    primarycolor: ''
  },
  logo: '',
  producers: {},
  batchDetails: {},
  participantData: {},
  sessionData: {},
  sessionSettingData: {// this defines the session settings as defined by trainer and are applicable to that session.
    showSessionControl: false,
    touchUpAppearance: false,
    showSelfView: false,
    stopCall: false,
    showConnectedTime: false,
    enableGroupIntereaction: true,
    forceMute: false,
    sessionMode: sessionModeType.NETWORKING_MODE,
    systemMute: false,
    micState: 'unmuted'
  },
  allGroup: [],
  myGroup: {},
  groupLeader: '',
  consoleDetails: {},
  participants: [],
  userStream: null,
  roomState: 'disconnected',
  backgroundEffect: false,
  sessionLiveFlag: false,
  bgImage: {},
  pendingBg: {},
  liveSessionActivityData: [],
  selectedAudioDevice: '',
  audioOutputDevices: [],
  selectedMicDevice: '',
  micInputDevices: [],
  audioSwitchInProgress: false,
  activeQueries: {},
  queryPopups: {},
  trainerJoinedtable: {
    isTrainerPresent: false,
    groupName: 0,
    TrainerJoinTableData: []
  },
  consumerPeers: [],
  groupLeaderNew: [],
};



export const fetchLiveSessionSettings = createAsyncThunk('livestreamSettings/fetchLiveSession', async (roomID: string) => {

  try {
    const response = await axiosInstance.post("/training/console_details", { id: roomID })
    const data = response.data.consoleDetails
    return data

  } catch (error) {
    throw error
  }
})

export const fetchLiveSessionActivityData = createAsyncThunk<any, { roomID: any }>(
  '/trainee/live_session_plan_preview_trainee',
  async ({ roomID }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/trainee/live_session_plan_preview_trainee", { session_map_id: roomID });
      const quickSetup = response.data.responseData.quickSetupActivities
      const prePlannedActivities = response.data.responseData.prePlannedActivities
      const data = [...quickSetup, ...prePlannedActivities]
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const fetchThemeColor = createAsyncThunk(
  'theme/fetchUiTheme',
  async ({ programId, userId }: any) => {
    try {
      const res: any = await axiosInstance.post('/theme/uitheme', {
        programId: programId,
        userId: userId,
        useCase: 'PROGRAM_EXECUTION',
        userType: 'LEARNER',
      });

      const themeui = res.ui ? JSON.parse(res.ui) : {}
      // assuming the API shape is { ui: 
      // { … } }
      const data = { theme: themeui, logo: res.logo }
      console.log('data', data);
      return data;

    } catch (error) {
      throw error
    }


  }
);






export const fetchOnlineParticipants = createAsyncThunk('livestreamSettings/fetchOnlineParticipants', async (roomID: string) => {
  try {
    const onlineParticipants = await axiosInstance.post('/session/get_online_count', { sessionMapId: roomID });
    return onlineParticipants.data;

  } catch (error) {
    throw error
  }
})

export const fetchSessionPlan = createAsyncThunk('livestreamSettings/fetchSessionPlan', async ({ sessionID, mapID }: { sessionID: string, mapID: string }) => {
  try {


    const payload = {
      session_id: sessionID,
      session_map_id: mapID,
      type: 'live',
      targetIndex: '',
      isQuickShuffling: false,
      isShuffling: false
    }

    const response: any = await axiosInstance.post('/trainer/session_plan_preview', payload);
    const data = response.data.responseData.prePlannedActivities;
    return data

  } catch (error) {
    throw error
  }
})
export const fetchBatchDetails = createAsyncThunk(
  '/trainee/batch/details',
  async ({ batchId }: { batchId: string }) => {
    try {
      const response = await axiosInstance.post(
        `/trainee/batch/details?batchId=${batchId}`,
        {},
      );
      return response.data;
    } catch (error) {
      console.log('Error', error);
      throw error;
    }
  }
);





// Creating the slice for livestreamSettings and adding a reducer function for updating the values in slice-Store
const livestreamSettings = createSlice({
  name: "livestreamsettings",
  initialState,
  reducers: {
    resetLiveStreamSettings: () => initialState,
    toggleSpeaker(state) {
      state.onSpeaker = !state.onSpeaker;
    },
    toggleAudio(state, action: PayloadAction<boolean | null>) {
      state.audio = action.payload;
    },
    toggleVideo(state, action: PayloadAction<boolean | null>) {
      state.video = action.payload;
    },
    toggleScreenPresentation(state) {
      state.isScreenPresented = !state.isScreenPresented;
    },
    toggleBackgroundEffect(state) {
      state.backgroundEffect = !state.backgroundEffect;
    },
    toggleSessionLiveFlag(state) {
      state.sessionLiveFlag = !state.sessionLiveFlag;
    },
    setSelectedAudioDevice(state, action) {
      state.selectedAudioDevice = action.payload;
    },
    setAudioOutputDevices(state, action) {
      state.audioOutputDevices = action.payload;
    },
    setSelectedMicDevice(state, action) {
      state.selectedMicDevice = action.payload;
    },
    setMicInputDevices(state, action) {
      state.micInputDevices = action.payload;
    },
    setAudioSwitchInProgress(state, action) {
      state.audioSwitchInProgress = action.payload;
    },
    updateSessionSettings: (
      state,
      action: PayloadAction<Partial<LiveStreamSettingsState["sessionSettingData"]>>
    ) => {
      state.sessionSettingData = {
        ...state.sessionSettingData,
        ...action.payload,
      };
    },
    raiseQuery: (state, action: PayloadAction<{ queryType: string; timeoutId?: NodeJS.Timeout }>) => {
      const { queryType, timeoutId } = action.payload;
      state.activeQueries[queryType] = {
        raisedAt: Date.now(),
        timeoutId,
      };
    },
    unraiseQuery: (state, action: PayloadAction<string>) => {
      const queryType = action.payload;
      const query = state.activeQueries[queryType];

      if (query?.timeoutId) {
        clearTimeout(query.timeoutId);
      }

      delete state.activeQueries[queryType];
      delete state.queryPopups[queryType];
    },
    clearAllQueries: (state) => {
      Object.values(state.activeQueries).forEach(query => {
        if (query.timeoutId) {
          clearTimeout(query.timeoutId);
        }
      });
      state.activeQueries = {};
      state.queryPopups = {};
    },
    showQueryPopup: (state, action: PayloadAction<{ queryType: string; originalQuery: string }>) => {
      const { queryType, originalQuery } = action.payload;
      state.queryPopups[queryType] = {
        visible: true,
        originalQuery,
        raisedAt: Date.now(),
      };
    },
    hideQueryPopup: (state, action: PayloadAction<string>) => {
      const queryType = action.payload;
      delete state.queryPopups[queryType];
    },
    hideAllQueryPopups: (state) => {
      state.queryPopups = {};
    },
    updateLiveStreamSettings: (
      state,
      action: PayloadAction<Partial<LiveStreamSettingsState>>
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setNewBadgeNotification(state) {
      state.hasNewBadge = true;
    },
    clearNewBadgeNotification(state) {
      state.hasNewBadge = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchLiveSessionSettings.fulfilled, (state, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.session = action.payload.session;
      state.consoleDetails = action.payload;
      if (state.session) {
        state.session.startTime = action.payload.session_start_time
        state.session.endTime = action.payload.session_end_time
        state.session.startDate = action.payload.session_start_date
        state.session.endDate = action.payload.session_end_date
        state.session.mapID = action.payload.id
      }
      state.batch = action.payload.batch
      state.trainer = action.payload.trainer
      state.muteParticipants = action.payload.session?.program?.programMeta?.muteParticipants || false;
      state.participantEntryType = action.payload.session?.program?.programMeta?.participantEntryType || "Auto Admit";
      // state.participantData = action.payload.batch.participants;
    })
      .addCase(fetchOnlineParticipants.fulfilled, (state, action: PayloadAction<any>) => {
        state.onlineParticipants = action.payload.onlineCount;
        state.participants = action.payload.onlineUserDetails;
      })
      .addCase(fetchSessionPlan.fulfilled, (state, action: PayloadAction<any>) => {
        if (state.session)
          state.session.segments = action.payload
      })
      .addCase(fetchThemeColor.fulfilled, (state, action: PayloadAction<any>) => {
        state.theme = action.payload
      })
      .addCase(fetchBatchDetails.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.batchDetails = action.payload
      })
      .addCase(fetchLiveSessionActivityData.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.liveSessionActivityData = action.payload
      })
      .addCase(fetchBatchDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
  }
});

// Exporting the reducer function so that we can access it from other components and use the useSelector hook for subscribing to the changes in data in store for the initial state.
export const { updateLiveStreamSettings, toggleSpeaker, resetLiveStreamSettings, updateSessionSettings, toggleAudio, toggleVideo, toggleScreenPresentation, toggleBackgroundEffect, toggleSessionLiveFlag, setAudioSwitchInProgress, setSelectedAudioDevice, setAudioOutputDevices, setSelectedMicDevice, setMicInputDevices, setNewBadgeNotification, clearNewBadgeNotification, raiseQuery, unraiseQuery, clearAllQueries, showQueryPopup, hideQueryPopup, hideAllQueryPopups } = livestreamSettings.actions;

export default livestreamSettings.reducer;
